'use client';
import { MouseEvent, FC } from 'react';

import Svg from '@/components/Svg';
import { MinusIcon, PlusIcon } from '@/icons';
import { twMerge } from 'tailwind-merge';
import Image from 'next/image';
import toggleCountLoader from '../../../public/images/global/toggleCountLoader.gif';
import Tooltip from '../Tooltip/Tooltip';

type PropTypes = {
  getCount: (...args: any) => void;
  defaultValue?: number;
  disabled?: boolean;
  rightAdorment?: string;
  minValue?: number;
  maxValue?: number;
  variant?: 'Default' | 'PLP' | 'FAVOURITES';
  OutofStock?: boolean;
  className?: string;
  disableAdd?: boolean;
  maxLimitMessage?: string;
  minLimitMessage?: string;
  isLoading?: boolean;
};

const ToggleCountField: FC<PropTypes> = ({
  getCount,
  defaultValue = 1,
  disabled,
  rightAdorment,
  minValue = 0,
  maxValue = 100,
  variant = 'Default',
  OutofStock = false,
  className,
  disableAdd = false,
  isLoading = false,
  maxLimitMessage = 'You have reached the maximum limit for adding this product',
  minLimitMessage = 'You cannot reduce the quantity any further',
}) => {
  const onIncrement = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const currentValue = defaultValue;
    if (currentValue >= maxValue) return;
    getCount(currentValue + 1);
  };

  const onDecrement = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const currentValue = defaultValue;
    if (currentValue <= minValue) return;
    getCount(currentValue - 1);
  };

  return (
    <div className='flex flex-col gap-1 w-full'>
      <div
        className={twMerge(
          'relative flex items-center gap-1',
          (variant === 'PLP' || variant === 'FAVOURITES') &&
            'h-full w-full justify-between rounded-full bg-pale-rose p-[0.35rem]',
          className
        )}>
        {/* Decrement Button */}
        <Tooltip
          message={minLimitMessage}
          hideTooltip={!(defaultValue <= minValue)}>
          <button
            id='decrease-qty'
            type='button'
            aria-label='decrement'
            className={twMerge(
              'rounded-full bg-pale-rose p-2',
              disabled ? 'opacity-50 cursor-not-allowed' : '',
              variant === 'PLP'
                ? 'bg-fig'
                : variant === 'FAVOURITES'
                  ? 'bg-[#D48A7A]'
                  : ''
            )}
            disabled={disabled}
            onClick={onDecrement}>
            <Svg
              fill='none'
              viewBox='0 0 16 16'
              className={twMerge(
                'h-4 w-4 stroke-fig stroke-1',
                variant === 'PLP'
                  ? 'stroke-pale-rose'
                  : variant === 'FAVOURITES'
                    ? 'stroke-black'
                    : ''
              )}>
              <MinusIcon />
            </Svg>
          </button>
        </Tooltip>
        {/* Input Loader or Count */}
        {(disabled && !OutofStock) || isLoading ? (
          <div className='flex items-center justify-center relative size-5'>
            <Image
              src={toggleCountLoader}
              alt='Loading...'
              fill
              unoptimized
              className='absolute inset-0 w-full object-contain'
            />
          </div>
        ) : (
          <input
            name='count'
            disabled
            readOnly
            className={twMerge(
              'w-5 bg-transparent text-center text-sm text-fig opacity-100 md:text-base',
              disabled ? 'opacity-50' : '',
              variant === 'FAVOURITES' && 'text-[#D48A7A]'
            )}
            value={defaultValue}
          />
        )}

        {rightAdorment && <span className='text-fig'>{rightAdorment}</span>}

        {/* Increment Button */}
        <Tooltip message={maxLimitMessage} hideTooltip={!disableAdd}>
          <button
            id='increase-qty'
            type='button'
            aria-label='increment'
            className={twMerge(
              'rounded-full bg-pale-rose p-2',
              disabled || disableAdd ? 'opacity-50 cursor-not-allowed' : '',
              variant === 'PLP'
                ? 'bg-fig'
                : variant === 'FAVOURITES'
                  ? 'bg-[#D48A7A]'
                  : ''
            )}
            disabled={disabled || disableAdd}
            onClick={onIncrement}>
            <Svg
              fill='none'
              viewBox='0 0 16 16'
              className={twMerge(
                'h-4 w-4 stroke-fig stroke-1',
                variant === 'PLP'
                  ? 'stroke-pale-rose'
                  : variant === 'FAVOURITES'
                    ? 'stroke-black'
                    : ''
              )}>
              <PlusIcon />
            </Svg>
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

export default ToggleCountField;
